import { useEjectSubQuiz } from "@/lib/hooks/slides/editor/useEjectSubQuiz";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import type { OtherSlideData, SubQuiz } from "@ai-teach/types";
import { SubQuizSection } from "../shared/SubQuizSection";

/**
 * Post-content quiz authoring for "other" slides. Thin Redux wrapper around the shared
 * `SubQuizSection` — mirrors `FluencyQuizSection` (reading), see that file's sibling for the form UI.
 */
export function OtherQuizSection() {
  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const { ejectSubQuiz } = useEjectSubQuiz();

  if (!slide) return null;

  const otherSlideData = slide.slideData as OtherSlideData;
  const quizzes = otherSlideData.subQuizzes ?? [];

  const commit = (next: SubQuiz[]) => {
    dispatch(
      updateSlide({ slideId: slide.id, slideData: { ...otherSlideData, subQuizzes: next } })
    );
  };

  // Create the standalone slide first; only drop the quiz here once it exists. The removal rides the
  // editor's next Save (see useEjectSubQuiz), so the slide must still be saved afterwards.
  const handleEject = async (quiz: SubQuiz) => {
    const ejected = await ejectSubQuiz(quiz, slide.lessonId, slide.tagIds, slide.domains);
    if (ejected) commit(quizzes.filter((q) => q.id !== quiz.id));
  };

  return (
    <SubQuizSection
      quizzes={quizzes}
      onChange={commit}
      label="Post-content quizzes"
      onEject={handleEject}
    />
  );
}
